import LinkButton from '@sats-group/ui-lib/react/link-button';
import Text from '@sats-group/ui-lib/react/text';
import cn from 'classnames';
import React from 'react';
import { useVideoControls } from '~/client/hooks/use-video-controls';
import { MediaType } from '~/shared/media-type-helper';
import BackgroundImage from '../background-image/background-image';
import BackgroundVideo from '../background-video/background-video';
import ContentContainer from '../content-container/content-container';
import Tagline from '../tagline/tagline';
import VideoTrigger from '../video-trigger/video-trigger';
import { HeroVariant, type Hero as Props } from './hero.types';

const Hero: React.FunctionComponent<Props> = ({
  media,
  variant = HeroVariant.Default,
  description,
  disclaimer,
  links,
  pause,
  play,
  tagline,
  title,
}) => {
  const {
    videoRef,
    isPlaying,
    play: playVideo,
    pause: pauseVideo,
    onPause,
    onTimeUpdate,
  } = useVideoControls();

  const hasVideo = media && media.type === MediaType.Video;

  return (
    <div className={cn('hero', `hero--${variant}`)}>
      <ContentContainer theme={ContentContainer.themes.wide}>
        <div className={cn('hero__inner', `hero__inner--${variant}`)}>
          <div
            className={cn(
              'hero__media-wrapper',
              `hero__media-wrapper--${variant}`
            )}
          >
            <div className="hero__media">
              {media.type === MediaType.Image ? (
                <BackgroundImage
                  {...media.props}
                  focusArea={BackgroundImage.focusAreas.Center}
                />
              ) : media.type === MediaType.Video ? (
                <React.Fragment>
                  <BackgroundVideo
                    {...media.props}
                    onPause={onPause}
                    onTimeUpdate={onTimeUpdate}
                    ref={videoRef}
                  />
                  <div className="hero__video-trigger">
                    {hasVideo ? (
                      <VideoTrigger
                        isPlaying={isPlaying}
                        play={playVideo}
                        pause={pauseVideo}
                        playLabel={play}
                        pauseLabel={pause}
                      />
                    ) : null}
                  </div>
                </React.Fragment>
              ) : null}
            </div>
          </div>
          <div
            className={cn(
              'hero__content-wrapper',
              `hero__content-wrapper--${variant}`
            )}
          >
            <ContentContainer theme={ContentContainer.themes.wide}>
              <div className={cn('hero__content', `hero__content--${variant}`)}>
                <div className="hero__content-part">
                  <div className="hero__title-wrapper">
                    {tagline ? (
                      <Tagline
                        {...tagline}
                        onDark={variant !== HeroVariant.Clean}
                        italic
                      />
                    ) : null}
                    <div>
                      <Text
                        elementName="h1"
                        size={Text.sizes.headline1}
                        theme={
                          variant === HeroVariant.SplitInfo
                            ? Text.themes.emphasis
                            : Text.themes.headline
                        }
                        italic={
                          variant === HeroVariant.SplitInfo ? false : true
                        }
                      >
                        {title}
                      </Text>
                    </div>
                  </div>
                  {description ? (
                    <Text
                      className={cn('hero__description', {
                        'hero__description--variant-clean':
                          variant === HeroVariant.Clean,
                      })}
                      elementName="p"
                      size={Text.sizes.large}
                      theme={Text.themes.normal}
                    >
                      {description}
                    </Text>
                  ) : null}
                </div>
                <div className="hero__content-part">
                  {links.length ? (
                    <div className="hero__actions">
                      {links.map((link, index) => (
                        <div className="hero__action" key={link.href}>
                          <LinkButton
                            {...link}
                            size={LinkButton.sizes.large}
                            variant={
                              index
                                ? variant === HeroVariant.Clean
                                  ? LinkButton.variants.ctaSecondary
                                  : LinkButton.variants.ctaSecondaryWhite
                                : LinkButton.variants.cta
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {disclaimer ? (
                    <Text elementName="p" size={Text.sizes.interface}>
                      {disclaimer}
                    </Text>
                  ) : null}
                </div>
              </div>
            </ContentContainer>
          </div>
        </div>
      </ContentContainer>
    </div>
  );
};

export default Hero;
